// 3D Icon tab — gently floating OneQode iconmark with optional dot-grid
// floor and theme toggle.
//
// Lighting and tonemap are inherited from setupSceneLights + applyTheme so
// the icon reads identically with the rest of the app. Material treatment
// is local to this view — softer matte than the default brand-blue accent
// elsewhere — because this is a presentation surface, not a placed object.
//
// Interaction:
//   • RMB drag (or LMB drag) — orbit
//   • Wheel — zoom in/out
//
// Toolbar (top bar):
//   • Theme: Dark / Light
//   • Floor: on/off
//   • Export PNG / Transparent / Copy / Copy Transparent

import React, { useCallback, useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { RoomEnvironment } from "three-stdlib";
import { THEMES, applyTheme, setupSceneLights, type ThemeMode } from "./lib/sceneAssets";
import { loadManifest } from "./lib/assetRegistry";
import { copyToClipboard, saveImage } from "./lib/exportScene";
import { Toast, type ToastState } from "./Toast";
import { applyLightingVersion, loadLightingVersion, saveLightingVersion, type LightingVersion } from "./lib/lightingVersion";

const BOB_AMPLITUDE = 0.06;
const BOB_PERIOD_SEC = 5.5;
const SWAY_AMPLITUDE_DEG = 2.5;
const SWAY_PERIOD_SEC = 7.0;
const ROT_SPEED_RAD_PER_SEC = (2 * Math.PI) / 18; // 18s/rev
const FLOOR_SIZE = 30;
const DOT_SPACING = 1.0;
const DOT_SIZE_FACTOR = 0.022;
const DOT_OPACITY = 0.45;
const ICON_FOCUS_Y = 1.4;
const CAMERA_DISTANCE_MIN = 3;
const CAMERA_DISTANCE_MAX = 18;
const CAMERA_DISTANCE_DEFAULT = 7;
const PITCH_MIN = 0.05;
const PITCH_MAX = Math.PI / 2 - 0.05;

function buildDotGridTexture(worldSize: number, spacing: number, colorHex: number, sizeFactor: number): THREE.CanvasTexture {
  const cellsPerAxis = Math.max(2, Math.round(worldSize / spacing));
  const px = 4096;
  const canvas = document.createElement("canvas");
  canvas.width = px;
  canvas.height = px;
  const c = canvas.getContext("2d")!;
  c.clearRect(0, 0, px, px);
  const col = new THREE.Color(colorHex);
  c.fillStyle = `rgba(${(col.r * 255) | 0}, ${(col.g * 255) | 0}, ${(col.b * 255) | 0}, 1)`;
  const step = px / cellsPerAxis;
  const r = Math.max(1.5, step * sizeFactor);
  for (let i = 0; i < cellsPerAxis; i++) {
    for (let j = 0; j < cellsPerAxis; j++) {
      c.beginPath();
      c.arc(i * step + step / 2, j * step + step / 2, r, 0, Math.PI * 2);
      c.fill();
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

/**
 * Soft-matte brand-blue treatment for the showcase icon. Higher roughness
 * + lower env intensity than the default accent so highlights don't punch
 * out, and a gentle emissive lift keeps the colour reading rich on dark
 * backgrounds without going neon.
 */
function applyShowcaseMaterial(group: THREE.Object3D): void {
  // Build a fresh, clean MeshStandardMaterial — no inherited onBeforeCompile,
  // no rim shader. Soft matte brand-blue with a gentle emissive lift so the
  // glyph reads richly against dark backgrounds without going neon.
  const matte = new THREE.MeshStandardMaterial({
    color: 0x0b72b7,
    metalness: 0,
    roughness: 0.85,
    envMapIntensity: 0.05,
    emissive: new THREE.Color(0x064b83),
    emissiveIntensity: 0.10,
  });
  matte.name = "icon-showcase-matte";
  group.traverse((c) => {
    const mesh = c as THREE.Mesh;
    if (!mesh.isMesh) return;
    mesh.material = matte;
  });
}

export default function IconShowcase() {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const groundRef = useRef<THREE.Mesh | null>(null);
  const dotGridRef = useRef<THREE.Mesh | null>(null);
  const lightsRefStash = useRef<ReturnType<typeof setupSceneLights> | null>(null);
  // Live camera state (refs so we can mutate every frame without React re-render).
  const camDistanceRef = useRef<number>(CAMERA_DISTANCE_DEFAULT);
  const camYawRef = useRef<number>(-Math.PI / 2 + 0.05);
  const camPitchRef = useRef<number>(Math.PI / 7);
  const dragRef = useRef<{ startX: number; startY: number; startYaw: number; startPitch: number } | null>(null);

  const [toast, setToast] = useState<ToastState>(null);
  const [theme, setTheme] = useState<ThemeMode>("dark");
  const [floorOn, setFloorOn] = useState<boolean>(true);
  const [lightingVersion, setLightingVersionState] = useState<LightingVersion>(() => loadLightingVersion());
  const setLightingVersion = useCallback((v: LightingVersion) => {
    setLightingVersionState(v);
    saveLightingVersion(v);
  }, []);
  const baseEnvIntensityRef = useRef<number>(THEMES.dark.environmentIntensity);

  useEffect(() => {
    if (!mountRef.current) return;
    const mount = mountRef.current;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.10;
    renderer.shadowMap.enabled = true;
    renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    renderer.setClearColor(THEMES.dark.bg);
    mount.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(THEMES.dark.bg);
    sceneRef.current = scene;

    const pmrem = new THREE.PMREMGenerator(renderer);
    scene.environment = pmrem.fromScene(RoomEnvironment(), 0.04).texture;
    scene.environmentIntensity = THEMES.dark.environmentIntensity;
    baseEnvIntensityRef.current = THEMES.dark.environmentIntensity;
    pmrem.dispose();
    applyLightingVersion(renderer, scene, lightingVersion, baseEnvIntensityRef.current);

    const lights = setupSceneLights(scene);
    lightsRefStash.current = lights;

    // Floor + dot grid — same look as Scene. Created here so applyTheme has
    // refs to mutate; the theme/floor effects below toggle visibility and
    // recolour without re-creating geometry.
    const groundGeo = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    const groundMat = new THREE.MeshStandardMaterial({
      color: 0x12151b,
      roughness: 0.92,
      metalness: 0,
      envMapIntensity: 0.10,
    });
    const ground = new THREE.Mesh(groundGeo, groundMat);
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);
    groundRef.current = ground;

    const dotGeo = new THREE.PlaneGeometry(FLOOR_SIZE, FLOOR_SIZE);
    const dotMat = new THREE.MeshBasicMaterial({
      map: buildDotGridTexture(FLOOR_SIZE, DOT_SPACING, 0xffffff, DOT_SIZE_FACTOR),
      transparent: true,
      depthWrite: false,
      opacity: DOT_OPACITY,
    });
    const dotGrid = new THREE.Mesh(dotGeo, dotMat);
    dotGrid.rotation.x = -Math.PI / 2;
    dotGrid.position.y = 0.005;
    scene.add(dotGrid);
    dotGridRef.current = dotGrid;

    applyTheme("dark", scene, renderer, ground, dotMat, lights.hemi, lights.key, lights.fill, lights.rim, lights.floorGlow);

    const camera = new THREE.PerspectiveCamera(36, 1, 0.1, 100);
    cameraRef.current = camera;
    const camTarget = new THREE.Vector3(0, ICON_FOCUS_Y, 0);
    function updateCamera() {
      const yaw = camYawRef.current;
      const pitch = THREE.MathUtils.clamp(camPitchRef.current, PITCH_MIN, PITCH_MAX);
      camPitchRef.current = pitch;
      const d = camDistanceRef.current;
      const cosP = Math.cos(pitch);
      const sinP = Math.sin(pitch);
      camera.position.set(
        camTarget.x + d * cosP * Math.cos(yaw),
        camTarget.y + d * sinP,
        camTarget.z + d * cosP * Math.sin(yaw),
      );
      camera.up.set(0, 1, 0);
      camera.lookAt(camTarget);
    }
    updateCamera();

    // ---- pointer drag → orbit ----
    function onPointerDown(e: PointerEvent) {
      // RMB or LMB both orbit (this view has no selection model so LMB is free).
      if (e.button !== 0 && e.button !== 2) return;
      e.preventDefault();
      (renderer.domElement as HTMLCanvasElement).setPointerCapture?.(e.pointerId);
      dragRef.current = {
        startX: e.clientX,
        startY: e.clientY,
        startYaw: camYawRef.current,
        startPitch: camPitchRef.current,
      };
    }
    function onPointerMove(e: PointerEvent) {
      const d = dragRef.current;
      if (!d) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;
      camYawRef.current = d.startYaw - dx * 0.0075;
      camPitchRef.current = THREE.MathUtils.clamp(d.startPitch + dy * 0.0075, PITCH_MIN, PITCH_MAX);
    }
    function onPointerUp(e: PointerEvent) {
      (renderer.domElement as HTMLCanvasElement).releasePointerCapture?.(e.pointerId);
      dragRef.current = null;
    }
    function onWheel(e: WheelEvent) {
      e.preventDefault();
      const factor = Math.exp(e.deltaY * 0.001);
      camDistanceRef.current = THREE.MathUtils.clamp(
        camDistanceRef.current * factor,
        CAMERA_DISTANCE_MIN,
        CAMERA_DISTANCE_MAX,
      );
    }
    function onContextMenu(e: MouseEvent) { e.preventDefault(); }

    const dom = renderer.domElement;
    dom.addEventListener("pointerdown", onPointerDown);
    dom.addEventListener("pointermove", onPointerMove);
    dom.addEventListener("pointerup", onPointerUp);
    dom.addEventListener("pointercancel", onPointerUp);
    dom.addEventListener("wheel", onWheel, { passive: false });
    dom.addEventListener("contextmenu", onContextMenu);

    const spinner = new THREE.Group();
    scene.add(spinner);

    let cancelled = false;
    let raf = 0;
    const start = performance.now() * 0.001;

    (async () => {
      await loadManifest();
      let icon: THREE.Object3D | null = null;
      try {
        const { getMaster } = await import("./lib/assetRegistry");
        const { SkeletonUtils } = await import("three-stdlib");
        const master = await getMaster("IconMark_Master");
        icon = SkeletonUtils.clone(master);
      } catch (e) {
        console.warn("[IconShowcase] failed to load IconMark_Master:", e);
      }
      if (!icon || cancelled) return;
      const box = new THREE.Box3().setFromObject(icon);
      const center = new THREE.Vector3();
      box.getCenter(center);
      icon.position.sub(center);
      icon.position.y += ICON_FOCUS_Y;
      icon.traverse((c) => {
        const m = c as THREE.Mesh;
        if (m.isMesh) {
          m.castShadow = true;
          m.receiveShadow = true;
        }
      });
      // Soft-matte brand-blue treatment local to the showcase. Avoids
      // touching the shared accent override used by the builders.
      applyShowcaseMaterial(icon);
      spinner.add(icon);
    })();

    function onResize() {
      const w = mount.clientWidth, h = mount.clientHeight;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    }
    onResize();
    const ro = new ResizeObserver(onResize);
    ro.observe(mount);

    function tick() {
      const now = performance.now() * 0.001;
      const t = now - start;

      spinner.rotation.y = t * ROT_SPEED_RAD_PER_SEC;
      const bob = Math.sin((t * (2 * Math.PI)) / BOB_PERIOD_SEC) * BOB_AMPLITUDE;
      spinner.position.y = bob;
      const swayRad = (SWAY_AMPLITUDE_DEG * Math.PI) / 180;
      spinner.rotation.z = Math.sin((t * (2 * Math.PI)) / SWAY_PERIOD_SEC) * swayRad;

      updateCamera();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(tick);
    }
    tick();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      ro.disconnect();
      dom.removeEventListener("pointerdown", onPointerDown);
      dom.removeEventListener("pointermove", onPointerMove);
      dom.removeEventListener("pointerup", onPointerUp);
      dom.removeEventListener("pointercancel", onPointerUp);
      dom.removeEventListener("wheel", onWheel);
      dom.removeEventListener("contextmenu", onContextMenu);
      renderer.dispose();
      if (mount.contains(renderer.domElement)) mount.removeChild(renderer.domElement);
    };
  }, []);

  // Theme — recolour ground / clear / lights via the same applyTheme helper.
  // Re-apply the lighting version after, since applyTheme rewrites exposure
  // and environmentIntensity as part of its palette swap.
  useEffect(() => {
    const renderer = rendererRef.current;
    const scene = sceneRef.current;
    const ground = groundRef.current;
    const dotGrid = dotGridRef.current;
    const lights = lightsRefStash.current;
    if (!renderer || !scene || !ground || !dotGrid || !lights) return;
    const dotMat = dotGrid.material as THREE.MeshBasicMaterial;
    applyTheme(theme, scene, renderer, ground, dotMat, lights.hemi, lights.key, lights.fill, lights.rim, lights.floorGlow);
    applyLightingVersion(renderer, scene, lightingVersion, baseEnvIntensityRef.current);
  }, [theme, lightingVersion]);

  // Floor visibility toggle. We hide both the ground plate AND the dot grid
  // so the icon floats against the bg colour.
  useEffect(() => {
    const ground = groundRef.current;
    const dotGrid = dotGridRef.current;
    if (!ground || !dotGrid) return;
    ground.visible = floorOn;
    dotGrid.visible = floorOn;
  }, [floorOn]);

  // ---- export controls ----
  const exportPng = useCallback(() => {
    const r = rendererRef.current, s = sceneRef.current, c = cameraRef.current;
    if (!r || !s || !c) return;
    saveImage(r, s, c, { includeBackground: true }, `oneqode-icon-${Date.now()}.png`);
  }, []);
  const exportPngTransparent = useCallback(() => {
    const r = rendererRef.current, s = sceneRef.current, c = cameraRef.current;
    if (!r || !s || !c) return;
    saveImage(r, s, c, { includeBackground: false }, `oneqode-icon-${Date.now()}-transparent.png`);
  }, []);
  const copyPng = useCallback(async (includeBackground: boolean) => {
    const r = rendererRef.current, s = sceneRef.current, c = cameraRef.current;
    if (!r || !s || !c) return;
    try {
      await copyToClipboard(r, s, c, { includeBackground });
      setToast({ id: Date.now(), message: includeBackground ? "Copied PNG" : "Copied transparent PNG" });
    } catch (e) {
      alert((e as Error).message);
    }
  }, []);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(null), 1800);
    return () => window.clearTimeout(timer);
  }, [toast?.id]);

  return (
    <div style={{ position: "absolute", inset: 0, overflow: "hidden" }}>
      <div ref={mountRef} style={{ position: "absolute", inset: 0 }} />
      <div style={styles.topBar}>
        <div style={styles.branding}>
          <span style={{ fontWeight: 600 }}>OneQode</span>
          <span style={{ color: "var(--muted)" }}>3D icon</span>
        </div>
        <div style={{ flex: 1 }} />
        {/* Lighting version — sits above Theme on the other tabs; here it
            comes first in the top bar so the order is consistent. */}
        <button
          style={{ ...styles.btn, outline: lightingVersion === "v1" ? "1px solid var(--accent)" : "none" }}
          onClick={() => setLightingVersion("v1")}
          title="Original tuning"
        >
          V1
        </button>
        <button
          style={{ ...styles.btn, outline: lightingVersion === "v2" ? "1px solid var(--accent)" : "none" }}
          onClick={() => setLightingVersion("v2")}
          title="Deeper blacks, stronger silhouette rim"
        >
          V2
        </button>
        <button
          style={{ ...styles.btn, outline: lightingVersion === "v3" ? "1px solid var(--accent)" : "none" }}
          onClick={() => setLightingVersion("v3")}
          title="Showroom-bright — chassis read as deep charcoal, highlights pop"
        >
          V3
        </button>
        {/* Theme toggle */}
        <button
          style={{ ...styles.btn, outline: theme === "dark" ? "1px solid var(--accent)" : "none" }}
          onClick={() => setTheme("dark")}
        >
          Dark
        </button>
        <button
          style={{ ...styles.btn, outline: theme === "light" ? "1px solid var(--accent)" : "none" }}
          onClick={() => setTheme("light")}
        >
          Light
        </button>
        <button
          style={{ ...styles.btn, outline: floorOn ? "1px solid var(--accent)" : "none" }}
          onClick={() => setFloorOn((p) => !p)}
        >
          Floor: {floorOn ? "on" : "off"}
        </button>
        <button style={styles.btn} onClick={exportPng}>Export PNG</button>
        <button style={styles.btn} onClick={exportPngTransparent}>Export Transparent PNG</button>
        <button style={styles.btn} onClick={() => void copyPng(true)}>Copy PNG</button>
        <button style={styles.btn} onClick={() => void copyPng(false)}>Copy Transparent PNG</button>
      </div>
      <Toast toast={toast} />
    </div>
  );
}

const PANEL_BORDER = "1px solid #1f1f24";
const styles: Record<string, React.CSSProperties> = {
  topBar: {
    position: "absolute", top: 12, left: 12, right: 12,
    display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap",
    pointerEvents: "none",
  },
  branding: { display: "flex", gap: 8, fontSize: 13, pointerEvents: "auto", color: "var(--fg)" },
  btn: {
    background: "var(--bg)", color: "var(--fg)", border: PANEL_BORDER,
    padding: "6px 10px", borderRadius: 3, cursor: "pointer", fontSize: 11,
    pointerEvents: "auto",
  },
};
